import {
	ReactNode,
	useContext,
	createContext,
	useMemo,
	useCallback,
} from 'react'

interface Options {
	replay?: boolean
	self?: boolean
}

type Context = {
	subscribe: <T>(
		channelID: string,
		callback: (payload: T) => void,
		options?: Options,
	) => () => void
	emit: <T>(channelID: string, payload: T) => void
}

const EventContext = createContext<Context>({
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	subscribe: () => () => {},
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	emit: () => {},
})

interface Channel {
	channelID: string
	subscribers: Map<
		string,
		// eslint-disable-next-line @typescript-eslint/ban-types
		{ callback: Function; options?: Options; contextID: string }
	>
	lastUpdate?: unknown
}

declare global {
	interface Window {
		eventManager?: Map<string, Channel>
	}
}

interface Props {
	children: ReactNode
	global?: boolean
}

export function EventProvider({ children, global = false }: Props) {
	const contextID = crypto?.randomUUID() || Math.random().toString(36)
	const eventManager = useMemo(() => {
		if (!global) return new Map<string, Channel>()
		if (!window.eventManager) window.eventManager = new Map<string, Channel>()
		return window.eventManager
	}, [global])

	const register = useCallback(
		(channelID: string) => {
			if (eventManager.has(channelID)) return

			const channel: Channel = {
				channelID,
				subscribers: new Map(),
			}
			eventManager.set(channelID, channel)
		},
		[eventManager],
	)

	const subscribe = useCallback(
		<T,>(
			channelID: string,
			callback: (payload: T) => void,
			options?: Options,
		) => {
			if (!eventManager.has(channelID)) register(channelID)

			const channel = eventManager.get(channelID)!

			const id = crypto?.randomUUID() || Math.random().toString(36)
			channel.subscribers.set(id, { callback, options, contextID })
			if (options?.replay) callback(channel.lastUpdate as T)
			return () => channel.subscribers.delete(id)
		},
		[contextID, eventManager, register],
	)

	const emit = useCallback(
		<T,>(channelID: string, payload: T) => {
			if (!eventManager.has(channelID)) register(channelID)

			const channel = eventManager.get(channelID)!
			channel.lastUpdate = payload
			// Persistenza lastUpdate
			channel.subscribers.forEach(
				({ callback, contextID: subContextID, options }) => {
					if (options?.self || contextID !== subContextID) {
						return callback(payload)
					}
				},
			)
		},
		[contextID, eventManager, register],
	)

	const value = useMemo(
		() => ({
			subscribe,
			emit,
		}),
		[subscribe, emit],
	)

	return <EventContext.Provider value={value}>{children}</EventContext.Provider>
}

EventProvider.defaultProps = {
	global: false,
}

/**
 * Event bus implementation from @gemini/utils
 * Duplicating this here until we can upgrade react version and we
 * can use it from the @gemini/utils library
 */
export const useEvent = () => useContext(EventContext)
